/**
 * Модалка создания нового чата (TG-стиль, 3-step flow):
 *   Step 1: выбор типа (Личное / Группа)
 *   Step 2 (личное): user picker → создать DM
 *   Step 2 (группа): multi-select юзеров с chips → next
 *   Step 3 (группа): avatar + название + admins → создать
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ORGS_KEY, TOPIC_ORGS_KEY, fetchMyOrgs } from "@/api/orgs";
import {
  fetchActiveUsers, createChatTopic, getOrCreateDm, userAvatarUrl, uploadTopicAvatar,
  type UserShort, type ChatTopic,
} from "@/api/dashboard";
import { Icon } from "@/components/prototype/Icon";
import { PACK_ICON_COUNT, packIconUrl, parseTopicIcon } from "@/data/animatedIcons";

type Step = "type" | "userPick" | "groupMembers" | "groupSettings";

const AVATAR_PALETTE = ["#4fae4e", "#3390ec", "#e99a12", "#9c7ce5", "#e85c5c", "#00acc1"];

function pickColor(seed: string): string {
  const hash = seed.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

function initials(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function UserAvatar({ user, size = "md" }: {
  user: { id: string; display_name: string | null; avatar_filename?: string | null };
  size?: "xs" | "sm" | "md" | "lg";
}) {
  const url = userAvatarUrl(user.id, user.avatar_filename);
  const cls = `mng-avatar${size !== "md" ? ` mng-avatar-${size}` : ""}`;
  if (url) return <img className={cls} src={url} alt=""/>;
  return (
    <div className={cls} style={{ background: pickColor(user.id) }}>
      {initials(user.display_name || "?")}
    </div>
  );
}

export function NewChatModal({
  myUserId, onClose, onCreated,
}: {
  myUserId: string;
  onClose: () => void;
  onCreated: (topic: ChatTopic) => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>("type");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [adminIds, setAdminIds] = useState<Set<string>>(new Set());
  const [groupName, setGroupName] = useState("");
  const [groupEmoji, setGroupEmoji] = useState<string>("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>("");
  // Группа создаётся в организации (03.10.2026): её материалы — только участникам этой организации.
  const orgsQ = useQuery({ queryKey: ORGS_KEY, queryFn: fetchMyOrgs, staleTime: 30_000 });
  const myOrgs = useMemo(() => (orgsQ.data?.orgs ?? []).filter(o => o.status === "member"), [orgsQ.data]);
  const [orgId, setOrgId] = useState<number | null>(null);
  const curOrg = orgId ?? myOrgs[0]?.id ?? null;
  const curOrgName = myOrgs.find(o => o.id === curOrg)?.name ?? "";

  const usersQ = useQuery({
    queryKey: ["users-active"],
    queryFn: fetchActiveUsers,
    staleTime: 5 * 60_000,
  });

  const otherUsers = useMemo<UserShort[]>(() => {
    const inOrg = (u: UserShort) => step !== "groupMembers" || curOrg == null
      || ((u as UserShort & { org_ids?: number[] }).org_ids ?? []).includes(curOrg);
    const all = (usersQ.data ?? []).filter(u => u.id !== myUserId && inOrg(u));
    if (!search.trim()) return all;
    const q = search.trim().toLowerCase();
    return all.filter(u => (u.display_name ?? "").toLowerCase().includes(q));
  }, [usersQ.data, myUserId, search, step, curOrg]);

  const selectedUsers = useMemo<UserShort[]>(() => {
    return (usersQ.data ?? []).filter(u => selectedIds.has(u.id));
  }, [usersQ.data, selectedIds]);

  function toggleUser(uid: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
        // Снимаем галочку админа
        setAdminIds(a => { const an = new Set(a); an.delete(uid); return an; });
      } else next.add(uid);
      return next;
    });
  }
  function toggleAdmin(uid: string) {
    setAdminIds(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  }

  const dmMut = useMutation({
    mutationFn: (partnerId: string) => getOrCreateDm(partnerId),
    onSuccess: (topic) => {
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
      onCreated(topic);
    },
    onError: (e: Error) => alert(e.message),
  });

  const groupMut = useMutation({
    mutationFn: async () => {
      const topic = await createChatTopic({
        name: groupName.trim(),
        kind: "private_group",
        icon: groupEmoji || null,
        member_ids: [...selectedIds],
        org_id: curOrg,
      });
      if (avatarFile) {
        try { await uploadTopicAvatar(topic.id, avatarFile); } catch (e) { console.error("avatar upload", e); }
      }
      return topic;
    },
    onSuccess: (topic) => {
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
      qc.invalidateQueries({ queryKey: TOPIC_ORGS_KEY });
      onCreated(topic);
    },
    onError: (e: Error) => alert(e.message),
  });

  function selectPersonal(uid: string) {
    if (dmMut.isPending) return;
    dmMut.mutate(uid);
  }

  function next() {
    if (step === "groupMembers" && selectedIds.size > 0) setStep("groupSettings");
  }
  function back() {
    if (step === "userPick" || step === "groupMembers") setStep("type");
    else if (step === "groupSettings") setStep("groupMembers");
  }

  const title = step === "type" ? "Новый чат"
    : step === "userPick" ? "Личное сообщение"
    : "Создать группу";

  const showFooter = step === "groupMembers" || step === "groupSettings";

  return (
    <div className="mng-modal-overlay show" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mng-modal" onClick={(e) => e.stopPropagation()}>
        <header className="mng-modal-header">
          {step !== "type" && (
            <button className="mng-modal-back" onClick={back} title="Назад">
              <Icon name="chevron_l" size={18}/>
            </button>
          )}
          <span className="mng-modal-title">{title}</span>
          <button className="mng-modal-close" onClick={onClose} title="Закрыть">
            <Icon name="x" size={16}/>
          </button>
        </header>
        <div className="mng-modal-body">

          {/* === STEP 1: TYPE === */}
          {step === "type" && (
            <div className="mng-choices">
              <button className="mng-choice" onClick={() => setStep("userPick")}>
                <div className="mng-choice-icon"><Icon name="user" size={22}/></div>
                <div className="mng-choice-info">
                  <div className="mng-choice-title">Личное сообщение</div>
                  <div className="mng-choice-desc">Напишите кому-то напрямую</div>
                </div>
              </button>
              <button className="mng-choice" onClick={() => setStep("groupMembers")}>
                <div className="mng-choice-icon"><Icon name="users" size={22}/></div>
                <div className="mng-choice-info">
                  <div className="mng-choice-title">Создать группу</div>
                  <div className="mng-choice-desc">Чат с несколькими участниками</div>
                </div>
              </button>
            </div>
          )}

          {/* === STEP 2 (personal): user picker === */}
          {step === "userPick" && (
            <>
              <div className="mng-search">
                <Icon name="search" size={16}/>
                <input
                  type="text"
                  placeholder="Поиск..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="mng-user-list">
                {otherUsers.length === 0 && <div className="mng-modal-empty">Нет пользователей</div>}
                {otherUsers.map(u => {
                  const name = u.display_name || "(без имени)";
                  return (
                    <div
                      key={u.id}
                      className="mng-user-list-item"
                      onClick={() => selectPersonal(u.id)}
                    >
                      <UserAvatar user={u}/>
                      <div className="mng-list-info">
                        <div className="mng-list-name">{name}</div>
                        <div className="mng-list-status">{u.position || ""}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* === STEP 2 (group): multi-select === */}
          {step === "groupMembers" && (
            <>
              {myOrgs.length > 1 && (
                <div className="mng-modal-group">
                  <label className="mng-modal-label">Чат для организации</label>
                  <div className="org-pick" role="radiogroup" aria-label="Организация">
                    {myOrgs.map(o => (
                      <button key={o.id} type="button" role="radio" aria-checked={curOrg === o.id}
                        className={`org-pick-opt${curOrg === o.id ? " is-on" : ""}`} title={o.name}
                        onClick={() => { if (curOrg !== o.id) { setOrgId(o.id); setSelectedIds(new Set()); setAdminIds(new Set()); } }}>
                        {o.name}
                      </button>
                    ))}
                  </div>
                  <div className="org-pick-hint">Группу, переписку и файлы увидят только участники «{curOrgName}». Позвать можно только их.</div>
                </div>
              )}
              <div className="mng-search">
                <Icon name="search" size={16}/>
                <input
                  type="text"
                  placeholder="Добавить участников..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
              {selectedUsers.length > 0 && (
                <div className="mng-chips">
                  {selectedUsers.map(u => {
                    const fn = (u.display_name || "?").split(" ")[0];
                    return (
                      <div key={u.id} className="mng-chip">
                        <UserAvatar user={u} size="xs"/>
                        <span>{fn}</span>
                        <button onClick={() => toggleUser(u.id)} title="Убрать">
                          <Icon name="x" size={10}/>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mng-user-list">
                {otherUsers.length === 0 && <div className="mng-modal-empty">Нет пользователей</div>}
                {otherUsers.map(u => {
                  const name = u.display_name || "(без имени)";
                  const isSel = selectedIds.has(u.id);
                  return (
                    <div
                      key={u.id}
                      className={`mng-user-list-item${isSel ? " selected" : ""}`}
                      onClick={() => toggleUser(u.id)}
                    >
                      <UserAvatar user={u}/>
                      <div className="mng-list-info">
                        <div className="mng-list-name">{name}</div>
                        <div className="mng-list-status">{u.position || ""}</div>
                      </div>
                      <div className="mng-checkbox"><Icon name="check" size={14}/></div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* === STEP 3 (group): settings === */}
          {step === "groupSettings" && (
            <>
              {myOrgs.length > 1 && (
                <div className="mng-modal-group">
                  <div className="org-pick-hint">Организация: <b>{curOrgName}</b></div>
                </div>
              )}
              <div className="mng-modal-group">
                <div className="mng-avatar-upload">
                  {avatarPreview ? (
                    <img className="mng-avatar mng-avatar-lg" src={avatarPreview} alt="" style={{ objectFit: "cover" }}/>
                  ) : (() => {
                    const p = parseTopicIcon(groupEmoji);
                    if (p.kind === "pack") {
                      return <img className="mng-avatar mng-avatar-lg" src={p.url} alt=""
                        style={{ objectFit: "contain", background: "transparent" }}/>;
                    }
                    return (
                      <div
                        className="mng-avatar mng-avatar-lg"
                        style={{ background: pickColor(groupName || "g"), fontSize: groupEmoji ? 36 : undefined }}
                      >
                        {groupEmoji || (groupName ? initials(groupName) : "?")}
                      </div>
                    );
                  })()}
                  <div className="mng-avatar-hint">
                    <label className="mng-avatar-upload-btn">
                      Загрузить фото
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          setAvatarFile(f);
                          setGroupEmoji("");
                          const reader = new FileReader();
                          reader.onload = () => setAvatarPreview(typeof reader.result === "string" ? reader.result : "");
                          reader.readAsDataURL(f);
                        }}
                      />
                    </label>
                    {avatarFile && (
                      <button type="button" className="mng-avatar-remove" onClick={() => { setAvatarFile(null); setAvatarPreview(""); }}>
                        Убрать
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="mng-modal-group">
                <label className="mng-modal-label">Иконка для аватарки (опц.)</label>
                <div
                  className="mng-emoji-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(48px, 1fr))",
                    gap: 6,
                    maxHeight: 280,
                    overflowY: "auto",
                  }}
                >
                  {Array.from({ length: PACK_ICON_COUNT }, (_, i) => i + 1).map(n => {
                    const value = `pack:${n}`;
                    const parsed = parseTopicIcon(groupEmoji);
                    const isActive = parsed.kind === "pack" && parsed.index === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        title={`Иконка #${n}`}
                        className={`mng-emoji-opt${isActive ? " active" : ""}`}
                        onClick={() => setGroupEmoji(isActive ? "" : value)}
                        style={{ aspectRatio: "1 / 1", padding: 3 }}
                      >
                        <img
                          src={packIconUrl(n)}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          width={40}
                          height={40}
                          style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mng-modal-group">
                <label className="mng-modal-label">Название группы</label>
                <input
                  type="text"
                  className="mng-modal-input"
                  placeholder="Например: Команда проекта"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="mng-modal-group">
                <label className="mng-modal-label">Администраторы (опционально)</label>
                <div className="mng-user-list" style={{ maxHeight: 180 }}>
                  {selectedUsers.map(u => {
                    const name = u.display_name || "?";
                    const isAdmin = adminIds.has(u.id);
                    return (
                      <div key={u.id} className="mng-user-list-item">
                        <UserAvatar user={u} size="sm"/>
                        <div className="mng-list-info">
                          <div className="mng-list-name">{name}</div>
                        </div>
                        <button
                          type="button"
                          className={`mng-admin-toggle${isAdmin ? " active" : ""}`}
                          onClick={() => toggleAdmin(u.id)}
                        >
                          Админ
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

        </div>
        {showFooter && (
          <div className="mng-modal-footer">
            {step === "groupMembers" ? (
              <button
                className="mng-btn mng-btn-primary"
                disabled={selectedIds.size === 0}
                onClick={next}
              >Далее</button>
            ) : (
              <button
                className="mng-btn mng-btn-primary"
                disabled={!groupName.trim() || groupMut.isPending}
                onClick={() => groupMut.mutate()}
              >{groupMut.isPending ? "Создание…" : "Создать"}</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
